/**
 * Per-server tool table with inline enable/risk-override/agent-access
 * controls. Each cell change fires a PATCH against
 * /api/mcp-servers/:serverId/tools/:toolId, awaits the response, and
 * propagates the server-confirmed row to the parent via
 * `onToolUpdated`. On error we surface a banner and leave the prior
 * cell value intact (the inputs are controlled by the parent's tool
 * list state). This is intentionally NOT optimistic v1 — it keeps the
 * displayed state in lock-step with the server so an admin doesn't see
 * a phantom "enabled" tool that the backend just rejected.
 *
 * The `agent_access` editor is intentionally minimal in v1 — a comma-
 * separated text input plus a one-click "Enable everything" affordance.
 *
 * That affordance goes through the bulk endpoint and sets BOTH `enabled`
 * and `agent_access: ['*']`. It previously only set the access
 * array, so an admin clicking it saw the Enabled column stay unticked and
 * reasonably concluded the button was broken — and agents still could not
 * call the tools, because the executor requires both.
 */
import { useMemo, useState } from "react";
import type { McpRiskClass, McpServer, McpServerTool } from "./types.js";

const RISK_LABELS: Record<McpRiskClass, string> = {
  read_only: "Read-only",
  mutate_external_reversible: "Reversible",
  mutate_external_irreversible: "Irreversible",
};

const RISK_BADGE_CLASS: Record<McpRiskClass, string> = {
  read_only: "bg-success/10 text-success",
  mutate_external_reversible: "bg-warning/10 text-warning",
  mutate_external_irreversible: "bg-error/10 text-error",
};

export interface McpToolListProps {
  server: McpServer;
  tools: McpServerTool[];
  onToolUpdated: (tool: McpServerTool) => void;
  /** Replaces the whole tool list after a bulk write. */
  onToolsReplaced: (tools: McpServerTool[]) => void;
}

interface PatchBody {
  enabled?: boolean;
  agentAccess?: string[];
  riskClassOverride?: McpRiskClass | null;
}

async function patchTool(
  serverId: string,
  toolId: string,
  body: PatchBody,
): Promise<McpServerTool> {
  const res = await fetch(
    `/api/mcp-servers/${serverId}/tools/${toolId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? `Update failed (${res.status})`);
  }
  const wrapped = (await res.json()) as { tool: McpServerTool };
  return wrapped.tool;
}

async function bulkUpdateTools(
  serverId: string,
  body: { enabled?: boolean; agentAccess?: string[] },
): Promise<McpServerTool[]> {
  const res = await fetch(`/api/mcp-servers/${serverId}/tools/bulk`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? `Update failed (${res.status})`);
  }
  const wrapped = (await res.json()) as { tools: McpServerTool[] };
  return wrapped.tools;
}

export function McpToolList({
  server,
  tools,
  onToolUpdated,
  onToolsReplaced,
}: McpToolListProps) {
  const sorted = useMemo(
    () => [...tools].sort((a, b) => a.toolName.localeCompare(b.toolName)),
    [tools],
  );
  const [error, setError] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);

  const liveTools = sorted.filter((t) => t.tombstonedAt === null);
  const allEnabled =
    liveTools.length > 0 &&
    liveTools.every((t) => t.enabled && t.agentAccess.includes("*"));

  async function applyPatch(toolId: string, body: PatchBody) {
    try {
      setError(null);
      const updated = await patchTool(server.id, toolId, body);
      onToolUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    }
  }

  /**
   * Select-all / clear-all. Both halves move together on purpose: an agent
   * can only call a tool when it is enabled AND the agent is in
   * `agent_access`, so setting one without the other is a no-op the admin
   * cannot see.
   */
  async function toggleAll(next: boolean) {
    if (bulkPending) return;
    setBulkPending(true);
    setError(null);
    try {
      const updated = await bulkUpdateTools(server.id, {
        enabled: next,
        agentAccess: next ? ["*"] : [],
      });
      onToolsReplaced(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBulkPending(false);
    }
  }

  if (sorted.length === 0) {
    return (
      <div className="material-dashed p-md text-sm">
        No tools discovered yet. Click "Refresh manifest" to retry.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center justify-between gap-md">
        <span className="text-sm text-text-secondary">
          {sorted.length} tool{sorted.length === 1 ? "" : "s"} ·{" "}
          {liveTools.filter((t) => t.enabled).length} enabled
        </span>
        <button
          type="button"
          className="text-xs text-accent hover:underline disabled:opacity-50"
          data-testid="mcp-select-all"
          disabled={bulkPending || liveTools.length === 0}
          onClick={() => void toggleAll(!allEnabled)}
        >
          {bulkPending
            ? "Applying…"
            : allEnabled
              ? "Disable every tool"
              : "Enable every tool for all agents"}
        </button>
      </div>
      {error && (
        <div role="alert" className="text-sm text-error">
          {error}
        </div>
      )}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-text-secondary">
            <th className="pb-xs font-medium">Tool</th>
            <th className="pb-xs font-medium">Default risk</th>
            <th className="pb-xs font-medium">Override</th>
            <th className="pb-xs font-medium">Agent access</th>
            <th className="pb-xs font-medium">Enabled</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((t) => (
            <tr
              key={t.id}
              data-testid={`mcp-tool-row-${t.toolName}`}
              className={
                t.tombstonedAt
                  ? "border-t border-dashed border-[var(--dash-border)] opacity-75"
                  : "border-t border-border"
              }
            >
              <td className="py-sm align-top">
                <div className="flex flex-col gap-xs">
                  <span className="font-medium">{t.toolName}</span>
                  {t.description && (
                    <span className="text-xs text-text-secondary line-clamp-2">
                      {t.description}
                    </span>
                  )}
                  {t.tombstonedAt && (
                    <span className="text-xs text-warning">
                      removed upstream — disabled
                    </span>
                  )}
                </div>
              </td>
              <td className="py-sm align-top">
                <span
                  className={`inline-block rounded px-xs py-[2px] text-xs ${RISK_BADGE_CLASS[t.riskClassDefault]}`}
                >
                  {RISK_LABELS[t.riskClassDefault]}
                </span>
              </td>
              <td className="py-sm align-top">
                <select
                  aria-label={`Risk override for ${t.toolName}`}
                  data-testid={`mcp-risk-override-${t.toolName}`}
                  value={t.riskClassOverride ?? ""}
                  onChange={(e) => {
                    const v = e.target.value === "" ? null : (e.target.value as McpRiskClass);
                    void applyPatch(t.id, { riskClassOverride: v });
                  }}
                  className="rounded border border-border bg-bg-input px-xs py-[2px] text-xs"
                >
                  <option value="">(use default)</option>
                  <option value="read_only">{RISK_LABELS.read_only}</option>
                  <option value="mutate_external_reversible">
                    {RISK_LABELS.mutate_external_reversible}
                  </option>
                  <option value="mutate_external_irreversible">
                    {RISK_LABELS.mutate_external_irreversible}
                  </option>
                </select>
              </td>
              <td className="py-sm align-top">
                <input
                  type="text"
                  aria-label={`Agent access for ${t.toolName}`}
                  data-testid={`mcp-agent-access-${t.toolName}`}
                  defaultValue={t.agentAccess.join(", ")}
                  onBlur={(e) => {
                    const next = e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean);
                    void applyPatch(t.id, { agentAccess: next });
                  }}
                  placeholder="* or comma-separated agent ids"
                  className="w-full rounded border border-border bg-bg-input px-xs py-[2px] text-xs"
                />
              </td>
              <td className="py-sm align-top">
                <label className="flex items-center gap-xs">
                  <input
                    type="checkbox"
                    aria-label={`Enable ${t.toolName}`}
                    data-testid={`mcp-enabled-${t.toolName}`}
                    checked={t.enabled}
                    disabled={t.tombstonedAt !== null}
                    onChange={(e) =>
                      void applyPatch(t.id, { enabled: e.target.checked })
                    }
                  />
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
